import type { Store } from "@reduxjs/toolkit";

/**
 * Deferred access to the Redux store instance, for the handful of modules
 * (GenericUtility, native bridge helpers) that need it OUTSIDE a React
 * component -- i.e. can't use `useSelector`/`useDispatch` -- but only ever
 * call `.dispatch()`/`.getState()` from inside a function, well after boot.
 *
 * Those modules must NOT `import store from "./UiDb"` directly: UiDb.ts is
 * the file that imports every slice to build the store, so a module deep in
 * a slice's own dependency chain importing the store BACK creates a real
 * module cycle (see resetAllState.ts's doc comment for the exact failure).
 *
 * This file has NO other imports, so it can never be part of a cycle.
 * UiDb.ts calls `setStore(store)` once, right after `configureStore(...)`;
 * everything else calls `getStore()`.
 */
let storeInstance: Store | null = null;

export const setStore = (store: Store) => {
  storeInstance = store;
};

export const getStore = (): Store => {
  if (!storeInstance) {
    throw new Error(
      "getStore() called before the store was created -- UiDb.ts must " +
      "finish evaluating (configureStore + setStore) before anything " +
      "reachable at module scope calls getStore()."
    );
  }
  return storeInstance;
};
